import { useContext } from 'react';
import { StoryData } from 'storyblok-js-client';

import styles from './ShoppingCartModal.module.scss';
import { GreenOkCircle } from '@base-components/Icons/GreenOkCircle';
import { RedX } from '@base-components/Icons/RedX';

import { CustomFooter } from './components/CustomFooter/CustomFooter';
import { Plan, PlanContractType } from 'types/plan.types';

import { ItemCardTop } from '@base-components/ItemCardTop/ItemCardTop';
import { Modal } from '@components/modals/Modal/Modal';
import { NotificationBar } from '@components/NotificationBar/NotificationBar';
import { CartContext } from '@context/cart';
import { useContentContext } from '@context/content';
import { useCartWithOffer } from '@hooks/cart/useCartWithOffer';
import { checkIfCartContractTypeSame } from '@helpers/checkIfCartContractTypeSame';
import { useGlobalContent } from '@hooks/useGlobalContent';
import { useLocale } from '@hooks/useLocale';
import { usePlanData } from '@modules/PDP/hooks/usePlanData';

const ShoppingCartModal = () => {
  const { isCartOpen, toggleCart } = useContext(CartContext);
  const { offerContent, plan } = usePlanData() as { offerContent: StoryData; plan: Plan };
  const { locale } = useLocale();
  const { data: globalContent } = useGlobalContent({
    locale,
  });
  const { data: cart, isLoading } = useCartWithOffer();
  const { allPlans } = useContentContext();

  if (isLoading || !cart) {
    return null;
  }

  const isContractTypeSame = checkIfCartContractTypeSame(cart, allPlans, plan);
  const isFinanceContractType = plan?.contractType === PlanContractType.Finance;

  const storyblokContent = globalContent?.['cart-modal']?.global?.[0];

  return (
    <Modal
      title={
        isContractTypeSame ? (
          <div className={styles.cartModalTitle}>
            <GreenOkCircle />
            {storyblokContent?.productAddedText}
          </div>
        ) : (
          <div className={styles.cartModalTitle}>
            <RedX />
            {storyblokContent?.cannotAddProductText}
          </div>
        )
      }
      size="large"
      closeModal={toggleCart}
      isOpen={isCartOpen}
      className={styles.cartModal}
      footer={{
        type: 'custom',
        component: (
          <CustomFooter
            continueShoppingText={storyblokContent?.continueShoppingText}
            goToCartText={storyblokContent?.goToCartText}
          />
        ),
      }}
    >
      <div className={styles.cartModalCartWrapper}>
        {!isContractTypeSame && (
          <div className={styles.cartModalFinance}>
            <NotificationBar
              variant="warning"
              content={
                isFinanceContractType
                  ? storyblokContent?.cannotAddRentalProductNotificationText
                  : storyblokContent?.cannotAddFinanceProductNotificationText
              }
              title={storyblokContent?.cannotAddProductNotificationTitleText}
            />
          </div>
        )}
        {isContractTypeSame && isFinanceContractType && (
          <div className={styles.cartModalFinance}>
            <NotificationBar variant="info" content={storyblokContent?.financeDurationSelectText} />
          </div>
        )}
        <ItemCardTop
          title={offerContent?.content.name}
          subtitle={offerContent?.content.subtitle}
          image={offerContent?.content.fullImage}
          imgAltText={offerContent?.content.name}
        />
      </div>
    </Modal>
  );
};

export default ShoppingCartModal;
